/* About Hermes (help-layer.md HL4), ported from renderAboutHermes (app.ts
   ~5205): the intake About's shape, app-wide. Two tabs — the theory of
   operation, and the permission model readable by everyone (the audit found
   "Record Keeper" defined only on the Admin-only Permissions page while My
   Work told ordinary users to go ask one). All copy comes from the help
   registry (help.ts), so this page and the ⓘ panels can never disagree.

   The full ARIA tabs pattern is the intake About's own wiring (UP7): each tab
   controls a named panel, and the rendered panel is labelled by the tab that
   opened it (PR-101 review). The tab selection lives in state.aboutTab, driven
   through the store's generic `update` action (the vanilla set-then-render). */
import type { JSX } from "react";
import { useAppState } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { CONCEPTS, badgeKey } from "../../help";
import { ProblemContactsBlock } from "./ProblemContacts";

/* the gated-actions list the Who tab spells out (app.ts GATED_ACTIONS) — the
   authority each carrier holds, in the app's own words */
const GATED_ACTIONS = [
  { action: "Grant and revoke organizer roles", carrier: "Admin (organizer role)" },
  {
    action: "Create and edit projects",
    carrier: "Record Keeper or Admin (organizer roles)",
  },
  {
    action: "Delete a project, history included",
    carrier: "Admin (organizer role)",
  },
  { action: "Add or correct a role in the catalog", carrier: "Admin (organizer role)" },
  {
    action: "Groom an intake effort and record its outcome",
    carrier: "Admin (organizer role)",
  },
  { action: "Change who to contact about problems", carrier: "Admin (organizer role)" },
];

/* one About disclosure block for a concept (aboutConceptBlock) — title + its
   plain-text paragraphs; React escapes the copy for us */
function AboutConceptBlock({ c }: { c: (typeof CONCEPTS)[number] }): JSX.Element {
  return (
    <section className="about-block">
      <h3>{c.title}</h3>
      {c.body.map((par, i) => (
        <p key={i}>{par}</p>
      ))}
    </section>
  );
}

export function AboutView(): JSX.Element {
  const { state, store } = useAppState();
  const how = state.aboutTab === "how";

  const howBody = (
    <>
      <section className="about-block">
        <h3>What Hermes is</h3>
        <p>
          Hermes is ASI's living project record, replacing stale Word charters and tribal
          knowledge. Each project records its charter, the roles it needs month by month (demand,
          in FTE) and its charge code. Every value carries its source and date, and a gap is shown
          as a gap, never filled with a guess.
        </p>
        <p>
          Upstream of the projects sits <b>Intake</b>: one form and one queue where any team
          registers a large effort, near-duplicates are joined, and intake managers groom each
          effort and record its outcome — often a project here. About intake explains the
          process in full.
        </p>
      </section>
      {/* who to tell when something looks wrong (problem-contacts.md PC7) */}
      <ProblemContactsBlock />
      {CONCEPTS.filter((c) => c.id !== "who-can-do-what").map((c) => (
        <AboutConceptBlock c={c} key={c.id} />
      ))}
      <section className="about-block">
        <h3>The colour key</h3>
        <div dangerouslySetInnerHTML={{ __html: badgeKey() }} />
        <p>
          Hermes never fabricates a value. A gap in the record is shown as a gap — red, orange, or a
          dash — not as a zero or a guess.
        </p>
      </section>
    </>
  );

  const whoBody = (
    <>
      <AboutConceptBlock c={CONCEPTS.find((c) => c.id === "who-can-do-what")!} />
      <section className="about-block">
        <h3>Gated actions</h3>
        <ul className="about-gates">
          {GATED_ACTIONS.map((g) => (
            <li key={g.action}>
              {g.action} — <b>{g.carrier}</b>
            </li>
          ))}
        </ul>
        <p>
          Everything not listed is readable by anyone signed in. If you need something changed and
          cannot change it yourself, the record's keeper (its team's, or an Admin) is who to ask —
          grants are visible on the Permissions page to those who hold it.
        </p>
      </section>
    </>
  );

  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>About Hermes.</b>
          <span>What this app is, how its record works, and who can do what.</span>
        </div>
      }
      footnote={
        "Every explanation here paraphrases a design decision in the repo's designs/ folder — the app's reasoning is written down, not folklore."
      }
    >
      <section className="card about-hermes">
        <div className="about-tabs" role="tablist" aria-label="About Hermes">
          <button
            className={`about-tab${how ? " selected" : ""}`}
            data-tab="how"
            role="tab"
            id="about-tab-how"
            aria-controls="about-panel-how"
            aria-selected={how}
            onClick={() => store.update({ aboutTab: "how" })}
          >
            How Hermes works
          </button>
          <button
            className={`about-tab${how ? "" : " selected"}`}
            data-tab="who"
            role="tab"
            id="about-tab-who"
            aria-controls="about-panel-who"
            aria-selected={!how}
            onClick={() => store.update({ aboutTab: "who" })}
          >
            Who can do what
          </button>
        </div>
        <div
          role="tabpanel"
          id={`about-panel-${how ? "how" : "who"}`}
          aria-labelledby={`about-tab-${how ? "how" : "who"}`}
        >
          {how ? howBody : whoBody}
        </div>
      </section>
    </ViewFrame>
  );
}
